/**
 * Очередь модерации отзывов.
 *
 * Отзыв не удаляется никогда — только скрывается, с причиной и автором
 * решения. Удалённый отзыв нельзя ни оспорить, ни восстановить, а рейтинг
 * после него меняется без следа: ровно та непрозрачность, против которой вся
 * платформа. Скрытый отзыв выпадает и из публичного списка, и из рейтинга —
 * вместе с оценкой, иначе «скрыть» означало бы спрятать текст, оставив звёзды.
 */
import type { ModeratedReview } from '@allswipes/client';
import { ApiError } from '@allswipes/client';
import { Badge, Button, Card, Empty, Icon, Skeleton, fmtShortDay, num } from '@allswipes/ui';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { api } from '../api';
import { Layout, ReadOnlyNote } from '../components/Layout';
import { useResource } from '../hooks';
import { useAdmin } from '../session';
import { useToast } from '../toast';

export function ReviewsScreen() {
  const [params, setParams] = useSearchParams();
  const only = params.get('only') ?? '';
  const data = useResource(() => api.adminReviews({ only, limit: 200 }), [only]);
  const { canModerate } = useAdmin();
  const toast = useToast();
  const [hiding, setHiding] = useState<ModeratedReview | null>(null);

  const setOnly = (value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set('only', value);
    else next.delete('only');
    setParams(next, { replace: true });
  };

  const unhide = async (review: ModeratedReview) => {
    try {
      await api.adminHideReview(review.claim_id, false);
      toast('Отзыв вернулся в публичный список');
      data.reload();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Не получилось');
    }
  };

  return (
    <Layout title="Отзывы" subtitle="скрыть можно только с причиной — решение видно в журнале">
      <ReadOnlyNote />

      <div className="chips" style={{ marginBottom: 14 }}>
        {[
          { key: '', label: 'Все' },
          { key: 'visible', label: 'Видимые' },
          { key: 'hidden', label: 'Скрытые' },
        ].map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`chip ${only === tab.key ? 'on' : ''}`}
            onClick={() => setOnly(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {data.loading && <Skeleton count={4} height={64} />}
      {data.data && data.data.reviews.length === 0 && (
        <Empty icon="star" title="Отзывов нет">
          Отзыв можно оставить только после подтверждения получения приза.
        </Empty>
      )}

      {data.data && data.data.reviews.length > 0 && (
        <Card>
          {data.data.reviews.map((review) => (
            <div className={`mrow ${review.hidden_at ? 'hidden' : ''}`} key={review.claim_id}>
              <div>
                <div className="who">
                  <span className="stars">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Icon key={n} name="star" size={12} className={n <= review.rating ? '' : 'off'} />
                    ))}
                  </span>
                  <strong style={{ color: 'var(--ink)' }}>{review.brand_name}</strong>
                  <span>· {review.campaign_name}</span>
                  <span>· {review.author}</span>
                  <span>· {fmtShortDay(review.created_at)}</span>
                  {review.prize_label && <Badge>{review.prize_label}</Badge>}
                  {review.hidden_at && <Badge tone="bad">скрыт</Badge>}
                </div>
                <div className="txt">{review.text || '— без текста, только оценка'}</div>
                {review.hidden_at && (
                  <div className="why">
                    Скрыт {fmtShortDay(review.hidden_at)}: {review.hidden_reason} · {review.hidden_by}
                  </div>
                )}
              </div>

              {canModerate && (
                <div className="acts">
                  {review.hidden_at ? (
                    <Button size="sm" onClick={() => void unhide(review)}>
                      Вернуть
                    </Button>
                  ) : (
                    <Button size="sm" variant="danger" onClick={() => setHiding(review)}>
                      Скрыть
                    </Button>
                  )}
                </div>
              )}
            </div>
          ))}
        </Card>
      )}

      {hiding && (
        <HideDialog
          review={hiding}
          onClose={() => setHiding(null)}
          onDone={() => {
            setHiding(null);
            data.reload();
          }}
        />
      )}
    </Layout>
  );
}

/** Причина обязательна: решение без причины нельзя ни проверить, ни оспорить. */
function HideDialog({
  review,
  onClose,
  onDone,
}: {
  review: ModeratedReview;
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.adminHideReview(review.claim_id, true, reason);
      toast('Отзыв скрыт — рейтинг пересчитан');
      onDone();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Не получилось');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ovl" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <div className="h2">Скрыть отзыв</div>
        <p className="sub" style={{ marginTop: 6 }}>
          Отзыв пропадёт из публичного списка, и его оценка перестанет считаться в рейтинге
          бренда. Запись останется: причину и автора решения видно в журнале.
        </p>
        <div className="krow" style={{ marginTop: 10 }}>
          <span className="k">{review.brand_name}</span>
          <span className="v">{num(review.rating)} ★</span>
        </div>
        <p className="sub" style={{ margin: '8px 0 12px' }}>
          «{review.text}»
        </p>
        <label className="lab" htmlFor="reason">
          Причина
        </label>
        <textarea
          id="reason"
          className="input"
          rows={3}
          placeholder="Например: личные оскорбления в адрес сотрудника"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        <div className="row" style={{ marginTop: 14, gap: 8 }}>
          <Button onClick={onClose}>Отмена</Button>
          <span className="spacer" />
          <Button variant="danger" disabled={busy || reason.trim().length < 4} onClick={submit}>
            Скрыть
          </Button>
        </div>
      </div>
    </div>
  );
}
